/** Fleet map: MapLibre view of every asset with a known position. */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Map, { Marker, NavigationControl, Popup, type ViewState } from "react-map-gl/maplibre";

import { type AssetFeatureCollection, useAssetMap } from "@/features/assets/queries";
import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { collectPages } from "@/shared/lib/paging";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { formatRelative } from "@/shared/lib/format";
import { Checkbox, Select } from "@/shared/ui/form";

/** Public demo style (no API key); `VITE_MAP_STYLE_URL` overrides it (empty string = unset). */
export const DEFAULT_MAP_STYLE: string =
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- "" (unset build-arg) must fall back
  import.meta.env.VITE_MAP_STYLE_URL || "https://demotiles.maplibre.org/style.json";

/** Red Sea coast fallback centre. */
const DEFAULT_VIEW: Pick<ViewState, "longitude" | "latitude" | "zoom"> = {
  longitude: 36.8,
  latitude: 25.7,
  zoom: 6,
};

/** Marker colour by asset status. */
export function markerColor(status: string): string {
  switch (status) {
    case "active":
      return "#059669";
    case "standby":
      return "#d97706";
    case "under_maintenance":
      return "#2563eb";
    case "out_of_service":
      return "#dc2626";
    default:
      return "#6b7280";
  }
}

/** Compute an initial view fitting the features (simple centroid + zoom heuristic). */
export function initialViewFor(
  fc: AssetFeatureCollection | undefined,
): Pick<ViewState, "longitude" | "latitude" | "zoom"> {
  if (!fc || fc.features.length === 0) {
    return DEFAULT_VIEW;
  }
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const f of fc.features) {
    const [lon, lat] = f.geometry.coordinates;
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  const span = Math.max(maxLon - minLon, maxLat - minLat, 0.01);
  const zoom = Math.max(3, Math.min(12, Math.log2(360 / span) - 1));
  return { longitude: (minLon + maxLon) / 2, latitude: (minLat + maxLat) / 2, zoom };
}

type Feature = AssetFeatureCollection["features"][number];

/** Fleet map page. */
export function FleetMapPage(): ReactElement {
  const { t } = useTranslation();
  const [vesselsOnly, setVesselsOnly] = useState(true);
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const map = useAssetMap(vesselsOnly, search.ownership);
  const [selected, setSelected] = useState<Feature | null>(null);
  const { can } = useAuth();
  const [showFaults, setShowFaults] = useState(false);
  const [severity, setSeverity] = useState("");
  const [faultStatus, setFaultStatus] = useState("");
  const [selectedFault, setSelectedFault] = useState<Schemas["FleetFaultOut"] | null>(null);
  const faults = useQuery({
    queryKey: ["requests", "map", severity, faultStatus, search.ownership],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/fleet/fault-map", {
            params: {
              query: {
                severity: severity || undefined,
                status: faultStatus || undefined,
                ownership: search.ownership,
                cursor,
                limit: 200,
              },
            },
          }),
        ),
      ),
    enabled: showFaults && can("requests.request.triage"),
  });
  const initial = useMemo(() => initialViewFor(map.data), [map.data]);

  const statuses = useMemo(() => {
    const s = new Set<string>();
    for (const f of map.data?.features ?? []) {
      s.add(f.properties.status);
    }
    return [...s];
  }, [map.data]);

  return (
    <div className="flex h-[calc(100dvh-7rem)] min-h-96 flex-col">
      <PageHeader
        title={t("map.title")}
        actions={
          <>
            <Select
              aria-label={t("assets.ownership")}
              value={search.ownership ?? ""}
              onChange={(event) => {
                const value = event.target.value;
                void navigate({
                  to: "/map",
                  search: {
                    ...search,
                    ownership: value === "owned" || value === "external" ? value : undefined,
                  },
                });
              }}
            >
              <option value="">{t("assets.ownershipScope.all")}</option>
              <option value="owned">{t("assets.ownershipScope.owned")}</option>
              <option value="external">{t("assets.ownershipScope.external")}</option>
            </Select>
            <Checkbox
              id="map-vessels-only"
              label={t("assets.vesselsOnly")}
              checked={vesselsOnly}
              onChange={(e) => setVesselsOnly(e.target.checked)}
            />
            {can("requests.request.triage") ? (
              <Checkbox
                id="map-faults"
                label={t("map.showFaults")}
                checked={showFaults}
                onChange={(event) => setShowFaults(event.target.checked)}
              />
            ) : null}
            {showFaults ? (
              <>
                <Select
                  aria-label={t("app.priority")}
                  value={severity}
                  onChange={(event) => setSeverity(event.target.value)}
                >
                  <option value="">{t("map.allSeverities")}</option>
                  {["low", "medium", "high", "critical"].map((value) => (
                    <option key={value} value={value}>
                      {t(`requests.severity.${value}`)}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label={t("app.status")}
                  value={faultStatus}
                  onChange={(event) => setFaultStatus(event.target.value)}
                >
                  <option value="">{t("map.allStatuses")}</option>
                  {[
                    "new",
                    "acknowledged",
                    "in_review",
                    "converted",
                    "resolved",
                    "closed",
                    "rejected",
                  ].map((value) => (
                    <option key={value} value={value}>
                      {t(`requests.status.${value}`)}
                    </option>
                  ))}
                </Select>
              </>
            ) : null}
          </>
        }
      />
      {showFaults && faults.isError ? (
        <ErrorState error={faults.error} onRetry={() => void faults.refetch()} />
      ) : null}
      {map.isPending ? (
        <LoadingState />
      ) : map.isError ? (
        <ErrorState error={map.error} onRetry={() => void map.refetch()} />
      ) : map.data.features.length === 0 && !faults.data?.some((fault) => fault.position) ? (
        <EmptyState message={t("map.noData")} />
      ) : (
        <div
          className="relative min-h-64 flex-1 overflow-hidden rounded-lg border"
          data-testid="fleet-map"
        >
          <Map
            key={`${initial.longitude}:${initial.latitude}`}
            initialViewState={initial}
            mapStyle={DEFAULT_MAP_STYLE}
            style={{ width: "100%", height: "100%" }}
            attributionControl={{ compact: true }}
          >
            <NavigationControl position="top-right" />
            {map.data.features.map((f) => (
              <Marker
                key={f.id}
                longitude={f.geometry.coordinates[0]}
                latitude={f.geometry.coordinates[1]}
                color={markerColor(f.properties.status)}
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setSelected(f);
                }}
              />
            ))}
            {showFaults
              ? faults.data?.flatMap((fault) =>
                  fault.position
                    ? [
                        <Marker
                          key={`fault-${fault.id}`}
                          longitude={fault.position.lon}
                          latitude={fault.position.lat}
                          color={fault.severity === "critical" ? "#dc2626" : "#d97706"}
                          onClick={(event) => {
                            event.originalEvent.stopPropagation();
                            setSelected(null);
                            setSelectedFault(fault);
                          }}
                        />,
                      ]
                    : [],
                )
              : null}
            {showFaults && selectedFault?.position ? (
              <Popup
                longitude={selectedFault.position.lon}
                latitude={selectedFault.position.lat}
                anchor="bottom"
                onClose={() => setSelectedFault(null)}
                closeOnClick={false}
              >
                <div className="space-y-1 text-sm">
                  <p>
                    {selectedFault.code} · {selectedFault.title}
                  </p>
                  <StatusBadge value={selectedFault.severity} namespace="requests.severity" />
                  <StatusBadge value={selectedFault.status} namespace="requests.status" />
                  <p>
                    <AssetReference
                      id={selectedFault.asset_id}
                      name={selectedFault.asset_name ?? "—"}
                    />{" "}
                    · {selectedFault.location_name}
                  </p>
                  <p>{t(`map.positionSource.${selectedFault.position_source}`)}</p>
                  {selectedFault.position_at ? (
                    <p>{formatRelative(selectedFault.position_at)}</p>
                  ) : null}
                  <Link
                    to="/requests/$requestId"
                    params={{ requestId: selectedFault.id }}
                    className="text-primary underline"
                  >
                    {t("map.viewFault")}
                  </Link>
                </div>
              </Popup>
            ) : null}
            {selected ? (
              <Popup
                longitude={selected.geometry.coordinates[0]}
                latitude={selected.geometry.coordinates[1]}
                anchor="bottom"
                offset={36}
                onClose={() => setSelected(null)}
                closeOnClick={false}
              >
                <div className="space-y-1 text-sm [overflow-wrap:anywhere]">
                  <div className="font-semibold">
                    <AssetReference
                      id={selected.id}
                      code={selected.properties.code}
                      name={selected.properties.name}
                    />
                  </div>
                  <StatusBadge value={selected.properties.status} namespace="assets.status" />
                  <div className="text-xs text-muted-foreground">
                    {t("map.lastSeen")}: {formatRelative(selected.properties.position_at)}
                  </div>
                  <Link
                    to="/assets/$assetId"
                    params={{ assetId: selected.id }}
                    className="text-primary hover:underline"
                  >
                    {t("map.viewAsset")} →
                  </Link>
                </div>
              </Popup>
            ) : null}
          </Map>
          <div className="absolute bottom-3 start-3 rounded-md border bg-background/90 p-2 text-xs shadow">
            <div className="mb-1 font-medium">{t("map.legend")}</div>
            {statuses.map((s) => (
              <div key={s} className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: markerColor(s) }}
                />
                {t(`assets.status.${s}`, { defaultValue: s })}
              </div>
            ))}
          </div>
        </div>
      )}
      {showFaults && faults.data?.some((fault) => !fault.position) ? (
        <section className="mt-3 max-h-40 overflow-y-auto rounded-md border p-3">
          <h2 className="text-sm font-medium">{t("map.unpositionedFaults")}</h2>
          <ul className="space-y-1 text-sm">
            {faults.data
              .filter((fault) => !fault.position)
              .map((fault) => (
                <li key={fault.id}>
                  <Link
                    to="/requests/$requestId"
                    params={{ requestId: fault.id }}
                    className="text-primary underline"
                  >
                    {fault.code} · {fault.title}
                  </Link>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
